import type { ApprovalRequest } from './policiesApprovalsApi.js';

export const WEEKLY_LIMIT_BLOCKED = {
  control: 'weekly-limit',
  architecturePath: 'POST /policies weeklyLimit',
  reason:
    '10-06 documents weeklyLimit on POST /policies. Show the weekly field. Send WEEKLY_CAP only while the live POLICY_RULE_TYPES list includes it.',
} as const;

/** Live operations — Wave 1 blocked copy removed. */
export const ASK_A_QUESTION_OPERATION = 'POST /business/v1/approvals/{id}/question' as const;
export const REVERT_POLICY_OPERATIONS = [
  'GET /business/v1/policies/{id}/versions',
  'POST /business/v1/policies/{id}/revert',
] as const;

export const DELEGATION_SCOPE_COPY =
  "Scope is this person's approval queue. A narrower department scope is not on the live create operation, so cover cannot be limited further here.";

const PENDING_STATES = new Set(['PENDING', 'OPEN', 'AWAITING', 'AWAITING_DECISION']);

export function isPendingApproval(approval: ApprovalRequest | null | undefined): boolean {
  const status = (approval?.status ?? approval?.state ?? '').toUpperCase();
  return PENDING_STATES.has(status) || !status;
}

export function sortApprovalsByExpiry(approvals: ApprovalRequest[]): ApprovalRequest[] {
  return [...approvals].sort((a, b) => {
    const ae = a.expiresAt ? Date.parse(a.expiresAt) : Number.POSITIVE_INFINITY;
    const be = b.expiresAt ? Date.parse(b.expiresAt) : Number.POSITIVE_INFINITY;
    return ae - be;
  });
}

export function nextPendingApprovalId(
  currentId: string | null | undefined,
  approvals: ApprovalRequest[],
): string | null {
  const pending = sortApprovalsByExpiry(
    approvals.filter((a) => a.id && a.id !== currentId && isPendingApproval(a)),
  );
  return pending[0]?.id ?? null;
}

export function isUrgentExpiry(expiresAt: string | null | undefined, now = Date.now()): boolean {
  if (!expiresAt) return false;
  const remaining = Date.parse(expiresAt) - now;
  return remaining > 0 && remaining <= 5 * 60 * 1000;
}

export function approvalRaisedAt(approval: ApprovalRequest | null | undefined): string | null {
  return approval?.requestedAt ?? approval?.createdAt ?? null;
}

export function formatOldestAge(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return '';
  const age = now - Date.parse(iso);
  if (!Number.isFinite(age) || age < 0) return '';
  const minutes = Math.floor(age / 60_000);
  if (minutes < 1) return 'oldest under a minute';
  if (minutes < 60) return `oldest ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `oldest ${hours}h`;
  const days = Math.floor(hours / 24);
  return days === 1 ? 'oldest 1 day' : `oldest ${days} days`;
}

export function oldestPendingTimestamp(approvals: ApprovalRequest[]): string | null {
  let oldestMs: number | null = null;
  let iso: string | null = null;
  for (const approval of approvals) {
    if (!isPendingApproval(approval)) continue;
    const raw = approvalRaisedAt(approval);
    if (!raw) continue;
    const t = Date.parse(raw);
    if (!Number.isFinite(t)) continue;
    if (oldestMs == null || t < oldestMs) {
      oldestMs = t;
      iso = raw;
    }
  }
  return iso;
}

function identityValues(record: Record<string, unknown> | null | undefined): string[] {
  if (!record) return [];
  return [
    record.accountId,
    record.userId,
    record.identityAccountId,
    record.identityId,
    record.id,
  ].filter((value): value is string => typeof value === 'string' && value.length > 0);
}

/** JWT `sub` is the identity account id on GET /employees (`accountId`), not the employee row id. */
export function viewerEmployeeIdFromList(
  employees: Array<Record<string, unknown> & { id?: string }>,
  tokenSub?: string | null,
): string | null {
  if (!tokenSub) return null;
  return employees.find((employee) => identityValues(employee).includes(tokenSub))?.id ?? null;
}

export type ApprovalViewerIdentity = {
  employeeId?: string | null;
  sub?: string | null;
};

export function filterOutOwnRequests(
  approvals: ApprovalRequest[],
  viewer: string | null | undefined | ApprovalViewerIdentity,
): ApprovalRequest[] {
  const identity: ApprovalViewerIdentity =
    typeof viewer === 'object' && viewer != null
      ? viewer
      : { employeeId: viewer ?? null, sub: null };
  const employeeId = identity.employeeId?.trim() || null;
  const sub = identity.sub?.trim() || null;
  if (!employeeId && !sub) return approvals;
  return approvals.filter((approval) => {
    const requesterEmp = approval.requestedByEmployeeId ?? null;
    const requester = approval.requestedBy ?? null;
    if (employeeId && (requesterEmp === employeeId || requester === employeeId)) return false;
    if (sub && (requester === sub || requesterEmp === sub)) return false;
    return true;
  });
}

export function mergeQueueExpiryInPlace(input: {
  previous: ApprovalRequest[];
  incoming: ApprovalRequest[];
  now?: number;
  localDecisionIds?: Set<string>;
}): { visible: ApprovalRequest[]; vanished: ApprovalRequest[]; expiredKept: ApprovalRequest[] } {
  const now = input.now ?? Date.now();
  const incomingIds = new Set(
    input.incoming.map((approval) => approval.id).filter((id): id is string => Boolean(id)),
  );
  const expiredKept: ApprovalRequest[] = [];
  const vanished: ApprovalRequest[] = [];
  for (const prev of input.previous) {
    if (!prev.id || incomingIds.has(prev.id)) continue;
    if (input.localDecisionIds?.has(prev.id)) continue;
    const exp = prev.expiresAt ? Date.parse(prev.expiresAt) : Number.NaN;
    if (Number.isFinite(exp) && exp <= now) {
      expiredKept.push({ ...prev, status: 'EXPIRED', state: 'EXPIRED' });
    } else {
      vanished.push(prev);
    }
  }
  return {
    visible: sortApprovalsByExpiry([...input.incoming, ...expiredKept]),
    vanished,
    expiredKept,
  };
}

export function remoteDecisionCopy(
  approval: ApprovalRequest | null | undefined,
  error?: { code?: string | null; message?: string | null },
): string {
  const access = classifyApprovalAccess({
    approval,
    errorCode: error?.code,
    errorMessage: error?.message,
  });
  const who = approval?.decidedByName ?? approval?.decidedBy ?? null;
  if (access.kind === 'decided' && who) {
    return `${who} decided a request that left this queue.`;
  }
  if (access.kind === 'delegated') {
    return 'A request was delegated and left this queue.';
  }
  if (access.kind === 'withdrawn') {
    return 'An employee withdrew a request.';
  }
  if (access.kind === 'expired') {
    return 'A request expired.';
  }
  return 'A request was decided elsewhere, or by a delegate.';
}

export function formatPausedRemaining(pauseRemainingMs: number | null | undefined): string {
  if (pauseRemainingMs == null || !Number.isFinite(pauseRemainingMs)) {
    return 'Clock paused while a question is outstanding';
  }
  const remaining = Math.max(0, pauseRemainingMs);
  const minutes = Math.floor(remaining / 60_000);
  const seconds = Math.floor((remaining % 60_000) / 1000);
  if (minutes >= 1) return `Clock paused · ${minutes} min ${seconds}s remaining`;
  return `Clock paused · ${seconds}s remaining`;
}

export function saveEffectiveFromLabel(at = new Date()): string {
  return at.toLocaleString('en-GB', {
    timeZone: 'Europe/London',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatTimeRemaining(
  expiresAt: string | null | undefined,
  now = Date.now(),
): string {
  if (!expiresAt) return 'No expiry given';
  const remaining = Date.parse(expiresAt) - now;
  if (!Number.isFinite(remaining)) return 'No expiry given';
  if (remaining <= 0) return 'Expired';
  const minutes = Math.floor(remaining / 60_000);
  const seconds = Math.floor((remaining % 60_000) / 1000);
  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m left`;
  }
  if (minutes >= 1) return `${minutes} min left`;
  return `${seconds}s left`;
}

export function expiryClockLabel(expiresAt: string | null | undefined): string {
  if (!expiresAt) return '';
  const t = new Date(expiresAt);
  if (Number.isNaN(t.getTime())) return '';
  return t.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

export function humanizeRuleType(ruleType: string): string {
  switch (ruleType) {
    case 'PER_ORDER_CAP':
      return 'over the per-order limit';
    case 'DAILY_CAP':
      return 'over the daily limit';
    case 'WEEKLY_CAP':
      return 'over the weekly limit';
    case 'MONTHLY_CAP':
      return 'over the monthly limit';
    case 'TIME_WINDOW':
      return 'outside eligible hours';
    case 'DAY_OF_WEEK':
      return 'outside eligible days';
    case 'CATEGORY_BLOCK':
      return 'includes an excluded category';
    case 'VERTICAL_BLOCK':
      return 'not an eligible vertical';
    case 'MAX_ITEMS':
      return 'too many items for the policy';
    case 'REQUIRES_APPROVAL_ABOVE':
      return 'above the approval threshold';
    default:
      return ruleType.replace(/_/g, ' ').toLowerCase();
  }
}

export function breachInWords(approval: ApprovalRequest | null | undefined): string {
  const reasons = approval?.triggerReasons ?? [];
  if (reasons.length === 0) {
    return approval?.summary?.trim() || 'Needs a decision';
  }
  return reasons.map(humanizeRuleType).join('; ');
}

export type StaleApprovalKind =
  'decided' | 'expired' | 'delegated' | 'withdrawn' | 'missing' | 'pending';

export function classifyApprovalAccess(input: {
  approval?: ApprovalRequest | null;
  errorCode?: string | null;
  errorMessage?: string | null;
  now?: number;
}): { kind: StaleApprovalKind; title: string; explanation: string } {
  const now = input.now ?? Date.now();
  const code = (input.errorCode ?? '').toUpperCase();
  const message = (input.errorMessage ?? '').toLowerCase();
  const status = (input.approval?.status ?? input.approval?.state ?? '').toUpperCase();

  if (code === 'ERR-APRV-3002' || /not an assigned approver|not authorized|delegat/.test(message)) {
    return {
      kind: 'delegated',
      title: 'This request is no longer yours to decide',
      explanation:
        'It was delegated away, or you are no longer the assigned approver. The person covering this queue will see it instead.',
    };
  }

  if (status === 'EXPIRED' || (code === 'ERR-APRV-3004' && /expir/.test(message))) {
    return {
      kind: 'expired',
      title: 'This request expired',
      explanation:
        'The employee was told the window closed. Approving now would not resume their checkout.',
    };
  }

  if (status === 'WITHDRAWN') {
    return {
      kind: 'withdrawn',
      title: 'The employee withdrew this request',
      explanation: 'There is nothing left to decide. Their basket is no longer held for approval.',
    };
  }

  if (
    status === 'APPROVED' ||
    status === 'DECLINED' ||
    status === 'REJECTED' ||
    status === 'DECIDED'
  ) {
    const who = input.approval?.decidedByName ?? input.approval?.decidedBy ?? null;
    return {
      kind: 'decided',
      title: who ? `${who} already decided this request` : 'This request was already decided',
      explanation:
        status === 'APPROVED' || status === 'DECIDED'
          ? 'Someone else approved it, including a delegate. The first decision stands.'
          : 'Someone else declined it. The first decision stands.',
    };
  }

  if (
    code === 'ERR-APRV-3004' ||
    /already decided/.test(message) ||
    (input.approval && !isPendingApproval(input.approval) && status)
  ) {
    return {
      kind: 'decided',
      title: 'This request was already decided',
      explanation:
        'The first decision stands. Opening it again does not change the outcome for the employee.',
    };
  }

  if (!input.approval && (code.includes('2001') || /not found/.test(message))) {
    return {
      kind: 'missing',
      title: 'This approval link is no longer valid',
      explanation:
        'The request may have been withdrawn or the link is stale. Open the queue for anything still waiting.',
    };
  }

  if (input.approval?.expiresAt && Date.parse(input.approval.expiresAt) <= now) {
    return {
      kind: 'expired',
      title: 'This request expired',
      explanation:
        'The employee was told the window closed. Approving now would not resume their checkout.',
    };
  }

  if (input.approval && isPendingApproval(input.approval)) {
    return {
      kind: 'pending',
      title: 'Waiting for your decision',
      explanation: '',
    };
  }

  if (!input.approval && input.errorMessage) {
    return {
      kind: 'missing',
      title: 'This request cannot be opened',
      explanation: input.errorMessage,
    };
  }

  return {
    kind: 'missing',
    title: 'This approval link is no longer valid',
    explanation:
      'Sign in again from the notification, or open the queue for anything still waiting.',
  };
}

export type BasketLine = {
  name: string;
  quantity?: number | null;
  amountMinor?: number | null;
};

export function basketLinesFromSummary(
  summary: Record<string, unknown> | null | undefined,
): BasketLine[] {
  if (!summary) return [];
  const raw = summary.lines ?? summary.items ?? summary.orderLines;
  if (!Array.isArray(raw)) return [];
  const lines: BasketLine[] = [];
  for (const row of raw) {
    if (!row || typeof row !== 'object') continue;
    const r = row as Record<string, unknown>;
    const name = String(r.name ?? r.title ?? r.productName ?? r.description ?? '').trim();
    if (!name) continue;
    const amount =
      typeof r.amountMinor === 'number'
        ? r.amountMinor
        : typeof r.lineTotalMinor === 'number'
          ? r.lineTotalMinor
          : typeof r.totalMinor === 'number'
            ? r.totalMinor
            : null;
    const quantity =
      typeof r.quantity === 'number' ? r.quantity : typeof r.qty === 'number' ? r.qty : null;
    lines.push({ name, quantity, amountMinor: amount });
  }
  return lines;
}

export function formatGbp(minor?: number | null, currency = 'GBP'): string {
  if (minor == null || !Number.isFinite(minor)) return '—';
  if (minor === 0) {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(0);
  }
  const abs = Math.abs(Math.round(minor));
  const major = Math.trunc(abs / 100) + (abs % 100) / 100;
  const formatted = new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(major);
  return minor < 0 ? `−${formatted.replace('−', '')}` : formatted;
}

export function poundsFromMinor(minor?: string | number | null): string {
  if (minor == null || minor === '') return '';
  const n = typeof minor === 'number' ? minor : Number(minor);
  if (!Number.isFinite(n)) return '';
  const abs = Math.abs(Math.round(n));
  const pounds = Math.trunc(abs / 100);
  const pence = abs % 100;
  return `${pounds}.${String(pence).padStart(2, '0')}`;
}
